import { useState, useEffect, useRef } from "react";
import { BookOpen, Calendar, MapPin, PenLine, Search, Shield, Users } from "lucide-react";
import { supabase } from "./supabase";
import { KO, fmtDate, sendPushToAll, sendPushToEmployee, sendPushToAdmin } from "./helpers";

export function SafetyManager({t, lang, isAdmin, empName, refreshKey}){
  const [records,setRecords]=useState([]);
  const [tab,setTab]=useState("list");
  const [editId,setEditId]=useState(null);
  const [employees,setEmployees]=useState([]);
  const [uploading,setUploading]=useState(null);
  const today=fmtDate(new Date());
  const curQ=Math.ceil((new Date().getMonth()+1)/3);
  const curYear=new Date().getFullYear();
  const def={quarter:`Q${curQ}`,year:String(curYear),date:today,location:"",duration:"60",topics:"",attendees:[],notes:"",signed_url:""};
  const [form,setForm]=useState(def);
  const [signModal,setSignModal]=useState(null); // {recId, empName}
  const [signing,setSigning]=useState(false);
  const canvasRef=useRef(null);
  const isDrawing=useRef(false);
  const [searchSafety,setSearchSafety]=useState("");
  const [showSearchSafety,setShowSearchSafety]=useState(false);
  const KO=lang==="kr"; const SP=lang==="es";

  useEffect(()=>{
    fetchRecs();
    supabase.from("employees").select("name,position,join_date,status").order("name").then(({data})=>{
      if(data) setEmployees(data);
    });
  },[refreshKey]);

  useEffect(()=>{
    if(signModal&&canvasRef.current){
      const ctx=canvasRef.current.getContext("2d");
      ctx.clearRect(0,0,canvasRef.current.width,canvasRef.current.height);
      ctx.strokeStyle="#1a1a1a"; ctx.lineWidth=2; ctx.lineCap="round";
    }
  },[signModal]);

  function startDraw(e){
    isDrawing.current=true;
    const c=canvasRef.current; const r=c.getBoundingClientRect();
    const x=(e.touches?e.touches[0].clientX:e.clientX)-r.left;
    const y=(e.touches?e.touches[0].clientY:e.clientY)-r.top;
    const ctx=c.getContext("2d"); ctx.beginPath(); ctx.moveTo(x*c.width/r.width,y*c.height/r.height);
    e.preventDefault();
  }
  function draw(e){
    if(!isDrawing.current) return;
    const c=canvasRef.current; const r=c.getBoundingClientRect();
    const x=(e.touches?e.touches[0].clientX:e.clientX)-r.left;
    const y=(e.touches?e.touches[0].clientY:e.clientY)-r.top;
    const ctx=c.getContext("2d"); ctx.lineTo(x*c.width/r.width,y*c.height/r.height); ctx.stroke();
    e.preventDefault();
  }
  function endDraw(){ isDrawing.current=false; }
  function clearCanvas(){ const c=canvasRef.current; c.getContext("2d").clearRect(0,0,c.width,c.height); }
  async function submitSignature(){
    const canvas=canvasRef.current;
    const imgData=canvas.getContext("2d").getImageData(0,0,canvas.width,canvas.height);
    if([...imgData.data].every(v=>v===0)){alert(KO?"서명해주세요.":SP?"Por favor firme primero.":"Please sign first.");return;}
    setSigning(true);
    const blob=await new Promise(r=>canvas.toBlob(r,"image/png"));
    const path=`safety_signed/${signModal.recId}_${signModal.empName}_${Date.now()}.png`;
    const{data:up}=await supabase.storage.from("hr-files").upload(path,blob,{contentType:"image/png",upsert:true});
    if(up){
      const{data:pu}=supabase.storage.from("hr-files").getPublicUrl(path);
      // signatures 컬럼: {empName: url, ...}
      const rec=records.find(r=>r.id===signModal.recId);
      const sigs={...(rec?.signatures||{}), [signModal.empName]:pu.publicUrl};
      await supabase.from("safety_training").update({signatures:sigs}).eq("id",signModal.recId);
      // 모든 서명 완료 시 관리자에게 알림
      const allSigned = (signModal.attendees||[]).every(n=>sigs[n]);
      if(allSigned){
        await sendPushToAdmin(supabase, "안전교육 서명 완료", `${signModal.quarter||""} ${signModal.year||""} 안전교육 서명이 모두 완료되었습니다.`, "Safety Training Signed", `All signatures for ${signModal.quarter||""} ${signModal.year||""} safety training have been completed.`, "/safety", {title:"Capacitación de seguridad firmada", body:`Se completaron todas las firmas de la capacitación de seguridad ${signModal.quarter||""} ${signModal.year||""}.`});
      }
      setSignModal(null); setSigning(false); fetchRecs();
    } else { setSigning(false); }
  }
  async function fetchRecs(){
    const{data}=await supabase.from("safety_training").select("*").order("date",{ascending:false});
    if(data) setRecords(data);
  }
  function sf(k,v){ setForm(p=>({...p,[k]:v})); }
  function toggleAtt(name){
    setForm(p=>({...p,attendees:p.attendees.includes(name)?p.attendees.filter(n=>n!==name):[...p.attendees,name]}));
  }
  function startEdit(r){ setForm({...def,...r,attendees:r.attendees||[]}); setEditId(r.id); setTab("add"); }
  async function save(){
    if(!form.date) return;
    const payload={quarter:form.quarter,year:parseInt(form.year),date:form.date,trainer:form.trainer||"",manager:form.manager||"",notes:form.notes||"",signed_url:form.signed_url||"",selected_emps:form.attendees||[],attendees:form.attendees||[],signatures:form.signatures||{}};
    if(editId){
      // 수정 시 새로 추가된 서명 대상자에게만 알림
      const prevRec = records.find(r=>r.id===editId);
      const prevAttendees = prevRec?.attendees || prevRec?.selected_emps || [];
      const newAttendees = (form.attendees||[]).filter(n=>!prevAttendees.includes(n));
      await supabase.from("safety_training").update(payload).eq("id",editId);
      for(const empName of newAttendees){
        await sendPushToEmployee(supabase, empName, "안전교육 서명 요청", `${form.quarter} ${form.year} 안전교육 확인 후 서명해주세요.`, "Safety Training", `Please review and sign the ${form.quarter} ${form.year} safety training.`, "/safety", {title:"Capacitación de seguridad", body:`Por favor revise y firme la capacitación de seguridad ${form.quarter} ${form.year}.`});
      }
    } else {
      await supabase.from("safety_training").insert([payload]);
      // 서명 대상자에게만 알림 (전체가 아닌 selected_emps)
      const attendees = form.attendees || [];
      for(const empName of attendees){
        await sendPushToEmployee(supabase, empName, "안전교육 서명 요청", `${form.quarter} ${form.year} 안전교육 확인 후 서명해주세요.`, "Safety Training", `Please review and sign the ${form.quarter} ${form.year} safety training.`, "/safety", {title:"Capacitación de seguridad", body:`Por favor revise y firme la capacitación de seguridad ${form.quarter} ${form.year}.`});
      }
    }
    setForm(def); setEditId(null); fetchRecs(); setTab("list");
  }
  async function del(id){
    if(window.confirm(KO?"삭제하시겠습니까?":SP?"¿Eliminar este registro?":"Delete this record?")){
      await supabase.from("safety_training").delete().eq("id",id); fetchRecs();
    }
  }
  async function uploadSigned(id,file){
    setUploading(id);
    try{
      const ext=file.name.split(".").pop();
      const path=`safety/${id}_${Date.now()}.${ext}`;
      const{error}=await supabase.storage.from("hr-files").upload(path,file,{upsert:true});
      if(error) throw error;
      const{data:u}=supabase.storage.from("hr-files").getPublicUrl(path);
      await supabase.from("safety_training").update({signed_url:u.publicUrl}).eq("id",id);
      fetchRecs(); alert(KO?"업로드 완료!":SP?"¡Cargado!":"Uploaded!");
    }catch(e){ alert("Error: "+e.message); }
    setUploading(null);
  }

  function downloadPdf(rec){
    const sigs = rec.signatures||{};
    const attendeeRows = (rec.attendees||[]).map((name,i)=>{
      const emp = employees.find(e=>e.name===name)||{};
      const sigUrl = sigs[name];
      const sigCell = sigUrl
        ? `<img src="${sigUrl}" style="max-width:100%;max-height:36px;object-fit:contain;display:block;margin:auto"/>`
        : "";
      return `<tr><td style="text-align:center">${i+1}</td><td>${name}</td><td>${emp.position||""}</td><td style="text-align:center;padding:2px 6px">${sigCell}</td><td></td></tr>`;
    });
    // 빈 행 없음 - 참석자 수대로만

    const qChecks = ["Q1 (Jan.-Mar.)","Q2 (Apr.-Jun.)","Q3 (Jul.-Sep.)","Q4 (Oct.-Dec.)"].map(q=>`
      <span style="margin-right:18px;display:inline-flex;align-items:center;gap:5px">
        <span style="display:inline-flex;align-items:center;justify-content:center;width:13px;height:13px;border:1.5px solid #333;border-radius:2px;font-size:10pt;font-weight:700;color:#111">${rec.quarter===q.slice(0,2)?"v":""}</span>${q}
      </span>`).join("");

    const html=`<!DOCTYPE html><html><head><meta charset="UTF-8">
    <title>Safety Training - ${rec.quarter} ${rec.year}</title>
    <style>
      @page{margin:0.75in 0.75in 0.75in; @bottom-center{content:"This executed document is legally binding under federal OSHA regulations and New Jersey state safety law. By signing, the Employee acknowledges completion of safety training and accepts responsibility for understanding and following all safety protocols. This document may be used as evidence in legal proceedings.";font-size:6.5pt;color:#555;font-family:Arial,sans-serif;text-align:center;}}
      body{font-family:Arial,sans-serif;font-size:9.5pt;color:#111;line-height:1.5;}
      .header{display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #999;padding-bottom:6px;margin-bottom:14px;}
      .logo{font-weight:900;font-size:12pt;letter-spacing:1px;}
      .docno{font-size:7.5pt;color:#777;}
      h1{text-align:center;font-size:15pt;font-weight:700;margin:0 0 16px;}
      .section{font-weight:700;font-size:10.5pt;border-bottom:1px solid #ccc;margin:14px 0 6px;padding-bottom:2px;text-transform:uppercase;letter-spacing:.5px;}
      .notice-box{border:1px solid #ccc;padding:10px 14px;font-size:8.5pt;color:#333;margin-bottom:14px;background:#fafafa;}
      .field-line{border-bottom:1px solid #555;display:inline-block;width:200px;height:16px;vertical-align:bottom;margin-left:6px;}
      .field-block{margin:8px 0;}
      ul{margin:3px 0 6px 20px;padding:0;}
      li{margin:2px 0;}
      .topic-title{font-weight:700;margin:8px 0 3px;}
      table{width:100%;border-collapse:collapse;font-size:9pt;}
      th{background:#2c3e50;color:#fff;padding:6px 8px;text-align:center;font-weight:700;}
      td{border:1px solid #aaa;padding:5px 8px;}
      tr:nth-child(even) td{background:#f5f7fa;}
      td:first-child{text-align:center;width:36px;background:#e8edf2;font-weight:600;}
      .cert-section{margin-top:14px;}
      .sig-grid{display:grid;grid-template-columns:1fr 1fr;gap:30px;margin-top:10px;}
      .sig-line{border-bottom:1px solid #555;margin:16px 0 4px;height:1px;}
      .sig-label{font-size:8pt;color:#666;}
    </style></head><body>
    <div class="header">
      <div class="logo">SPRINGBLINDS</div>
      <div class="docno">Document No. SR-QSTA-001 | Version: 1.0</div>
    </div>
    <h1>Quarterly Safety Training Acknowledgment</h1>

    <div class="notice-box">
      <b>LEGAL NOTICE</b><br/>
      This executed document is legally binding under federal OSHA regulations and New Jersey state safety law. By signing, the Employee acknowledges completion of safety training and accepts responsibility for understanding and following all safety protocols. This document may be used as evidence in legal proceedings.
    </div>

    <div class="section">Training Information</div>
    <div class="field-block">Training Quarter: &nbsp; ${qChecks}</div>
    <div class="field-block">Training Year: <span class="field-line">${rec.year||""}</span></div>
    <div class="field-block">Training Date: <span class="field-line">${rec.date||""}</span></div>
    ${rec.location?`<div class="field-block">Training Location: <span class="field-line">${rec.location}</span></div>`:""}

    <div class="section">Training Topics Covered</div>
    <div class="topic-title">Required Training Topics</div>
    <div class="topic-title">General Safety Rules</div>
    <ul><li>Workplace safety rules</li><li>Personal protective equipment (PPE) usage</li><li>Emergency exits and evacuation routes</li></ul>
    <div class="topic-title">Machine Safety</div>
    <ul><li>Safe operation of cutting machines</li><li>Press and assembly equipment safety</li><li>Lockout/Tagout (LOTO) procedures</li><li>Response to machine malfunctions</li></ul>
    <div class="topic-title">Height Safety</div>
    <ul><li>Safe ladder usage</li><li>Precautions for high rack work</li><li>Fall protection equipment</li><li>Safe loading and unloading procedures</li></ul>
    <div class="topic-title">Material Handling</div>
    <ul><li>Heavy lifting techniques</li><li>Forklift safety</li><li>Stacking and storage safety</li></ul>
    <div class="topic-title">Chemical Safety</div>
    <ul><li>Safe handling of adhesives and solvents</li><li>Ventilation and protective equipment</li></ul>
    <div class="topic-title">First Aid</div>
    <ul><li>Injury response procedures</li><li>First aid kit locations</li><li>Accident reporting procedures</li></ul>
    <div class="topic-title">Fire Safety</div>
    <ul><li>Fire extinguisher locations and usage</li><li>Fire emergency response</li><li>Fire alarm system</li></ul>
    ${rec.topics?`<div class="field-block">Other: <span class="field-line">${rec.topics}</span></div>`:'<div class="field-block">Other: <span class="field-line"></span></div>'}

    <div class="section">Attendance List and Signatures</div>
    <table>
      <thead><tr><th>#</th><th>Employee Name</th><th>Position</th><th>Signature</th><th>Date</th></tr></thead>
      <tbody>${attendeeRows.join("")}</tbody>
    </table>

    <div class="cert-section">
      <div class="section">Trainer Certification</div>
      <p style="font-size:8.5pt">I certify that I have conducted the above safety training and confirm that all attendees understood the training content.</p>
      <div class="field-block">Trainer Name: <span class="field-line"></span></div>
      <div class="field-block">Trainer Signature: <span class="field-line"></span></div>
      <div class="field-block">Date: <span class="field-line"></span></div>
    </div>

    <div class="cert-section">
      <div class="section">Manager's Approval</div>
      <div class="field-block">Manager Name: <span class="field-line"></span></div>
      <div class="field-block">Manager Signature: <span class="field-line"></span></div>
      <div class="field-block">Date: <span class="field-line"></span></div>
    </div>

    <div class="cert-section">
      <div class="section">Company Record</div>
      <div class="field-block">Next Training Due: <span class="field-line"></span></div>
    </div>

    <script>window.onload=()=>window.print();</script>
    </body></html>`;
    const w=window.open("","_blank"); w.document.write(html); w.document.close();
  }

  return(
    <>
      <div className="ph">
        <div className="pt"><Shield size={20}/> {KO?"분기별 안전교육 관리":SP?"Capacitación trimestral de seguridad":"Quarterly Safety Training"}</div>
        <div className="ps">{KO?"분기별 안전교육을 기록하고 서명된 문서를 관리합니다.":SP?"Registre las capacitaciones trimestrales de seguridad y administre los documentos firmados":"Record quarterly safety training and manage signed documents"}</div>
      </div>
      <div className="tabs">
        <button className={`tab ${tab==="list"?"active":""}`} onClick={()=>{setTab("list");setEditId(null);setForm(def);}}>
          {KO?"기록 조회":SP?"Ver registros":"View Records"}
        </button>
        {isAdmin&&<button className={`tab ${tab==="add"?"active":""}`} onClick={()=>{setTab("add");setEditId(null);setForm(def);}}>
          + {KO?"새 기록":SP?"Nuevo registro":"New Record"}
        </button>}
      </div>

      {tab==="add"&&(
        <div className="card">
          <div className="ct">{editId?(KO?"기록 수정":SP?"Editar registro":"Edit Record"):(KO?"안전교육 기록":SP?"Agregar capacitación de seguridad":"Add Safety Training")}</div>
          <div className="fg">
            <div className="fl">
              <label>{KO?"분기":SP?"Trimestre":"Quarter"}</label>
              <select value={form.quarter} onChange={e=>sf("quarter",e.target.value)}>
                {["Q1","Q2","Q3","Q4"].map(q=><option key={q}>{q}</option>)}
              </select>
            </div>
            <div className="fl">
              <label>{KO?"연도":SP?"Año":"Year"}</label>
              <input type="number" value={form.year} onChange={e=>sf("year",e.target.value)}/>
            </div>
            <div className="fl">
              <label>{KO?"교육일":SP?"Fecha de capacitación":"Training Date"}</label>
              <input type="date" value={form.date} onChange={e=>sf("date",e.target.value)}/>
            </div>
            <div className="fl">
              <label>{KO?"장소":SP?"Lugar":"Location"}</label>
              <input value={form.location} onChange={e=>sf("location",e.target.value)}/>
            </div>
            <div className="fl">
              <label>{KO?"교육 시간(분)":SP?"Duración (min)":"Duration (min)"}</label>
              <input type="number" value={form.duration} onChange={e=>sf("duration",e.target.value)}/>
            </div>
            <div className="fl ff">
              <label>{KO?"추가 교육 주제 (Other)":SP?"Temas adicionales (Otros)":"Additional Topics (Other)"}</label>
              <input value={form.topics} onChange={e=>sf("topics",e.target.value)} placeholder={KO?"예: 신규 장비 안전교육...":SP?"Ej.: seguridad con equipo nuevo...":"e.g. New equipment safety..."}/>
            </div>
            <div className="fl ff">
              <label>{KO?"메모":SP?"Notas":"Notes"}</label>
              <input value={form.notes} onChange={e=>sf("notes",e.target.value)}/>
            </div>
          </div>
          <div style={{marginBottom:16}}>
            <label style={{fontWeight:600,fontSize:13,color:"var(--tx)",display:"block",marginBottom:8,marginTop:16}}>
              {KO?"참석자 선택":SP?"Seleccionar asistentes":"Select Attendees"}
            </label>
            <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
              {[...employees].filter(e=>e.status!=="Inactive").sort((a,b)=>{const yA=a.join_date?(new Date()-new Date(a.join_date)):0;const yB=b.join_date?(new Date()-new Date(b.join_date)):0;return yB-yA;}).map(emp=>(
                <label key={emp.name} style={{
                  display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13,
                  padding:"5px 14px",borderRadius:20,
                  background:form.attendees.includes(emp.name)?"var(--ac)":"var(--sf2)",
                  color:form.attendees.includes(emp.name)?"#fff":"var(--tx)",
                  border:"1px solid var(--bd)",transition:"all .15s"
                }}>
                  <input type="checkbox" checked={form.attendees.includes(emp.name)} onChange={()=>toggleAtt(emp.name)} style={{display:"none"}}/>
                  {emp.name}
                </label>
              ))}
            </div>
          </div>
          <div style={{marginTop:14,display:"flex",gap:8}}>
            <button className="btn bp" onClick={save}>{KO?"저장":SP?"Guardar":"Save"}</button>
            <button className="btn bsec" onClick={()=>{setTab("list");setEditId(null);setForm(def);}}>{KO?"취소":SP?"Cancelar":"Cancel"}</button>
          </div>
        </div>
      )}

      {tab==="list"&&(
        <>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
          <button onClick={()=>{setShowSearchSafety(s=>!s);if(showSearchSafety)setSearchSafety("");}} style={{background:"var(--sf2)",border:"1px solid var(--bd)",borderRadius:8,padding:"6px 10px",cursor:"pointer",fontSize:16,color:"var(--tx2)",lineHeight:1}}><Search size={15}/></button>
          {showSearchSafety&&<input autoFocus value={searchSafety} onChange={e=>setSearchSafety(e.target.value)}
            placeholder={KO?"분기, 연도, 참석자 검색...":SP?"Buscar trimestre, año, asistentes...":"Search quarter, year, attendees..."}
            style={{flex:1,padding:"6px 14px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:13}}
          />}
        </div>
        {records.filter(rec=>{if(!searchSafety)return true;const q=searchSafety.toLowerCase();return rec.quarter?.toLowerCase().includes(q)||String(rec.year).includes(q)||(rec.attendees||[]).some(a=>a.toLowerCase().includes(q));}).length===0
          ?<div className="card"><div className="empty">{KO?"기록 없음":SP?"Sin registros":"No records"}</div></div>
          :records.filter(rec=>{if(!searchSafety)return true;const q=searchSafety.toLowerCase();return rec.quarter?.toLowerCase().includes(q)||String(rec.year).includes(q)||(rec.attendees||[]).some(a=>a.toLowerCase().includes(q));}).map(rec=>(
            <div className="card" key={rec.id}>
              {(()=>{
                const sigs=rec.signatures||{};
                const atts=rec.attendees||[];
                const signedCount=atts.filter(n=>sigs[n]).length;
                const allSigned=atts.length>0&&signedCount===atts.length;
                const myUnsigned=isAdmin?null:atts.find(n=>n===empName&&!sigs[n]);
                const sortByTenure=(arr)=>[...arr].sort((a,b)=>{
                  const eA=employees.find(e=>e.name===a);
                  const eB=employees.find(e=>e.name===b);
                  const yA=eA?.join_date?(new Date()-new Date(eA.join_date)):0;
                  const yB=eB?.join_date?(new Date()-new Date(eB.join_date)):0;
                  return yB-yA;
                });
                const unsignedSorted=sortByTenure(atts.filter(n=>!sigs[n]));
                return <>
                  {/* 상단: 제목 + 수정/삭제 */}
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                    <div style={{fontWeight:700,fontSize:15}}>
                      {rec.quarter} {rec.year}
                      <span style={{marginLeft:8,padding:"2px 10px",borderRadius:20,fontSize:11,fontWeight:700,
                        background:allSigned?"rgba(5,150,105,.12)":"rgba(217,119,6,.12)",
                        color:allSigned?"var(--gr)":"var(--ye)"}}>
                        {allSigned?(KO?"서명 완료":SP?"Todos firmaron":"All Signed"):`${signedCount}/${atts.length} ${KO?"서명":SP?"Firmado":"Signed"}`}
                      </span>
                    </div>

                  </div>
                  {/* 날짜 등 메타 */}
                  <div style={{fontSize:12,color:"var(--tx3)",marginBottom:8}}>
                    <Calendar size={11}/> {rec.date}
                    {rec.location&&<span> · <MapPin size={11} style={{marginRight:3}}/>{rec.location}</span>}
                    {rec.duration&&<span> · ⏱ {rec.duration}{KO?"분":SP?"min":"min"}</span>}
                  </div>
                  {rec.topics&&<div style={{fontSize:12,color:"var(--tx3)",marginBottom:8}}><BookOpen size={12} style={{marginRight:4}}/>{rec.topics}</div>}
                  {/* 참석자 배지 + 서명버튼 좌우 레이아웃 */}
                  <div style={{display:"flex",gap:16,alignItems:"flex-start",flexWrap:"wrap"}}>
                    {/* 좌: 참석자 배지 3명씩 */}
                    {atts.length>0&&(
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,color:"var(--tx3)",marginBottom:4}}><Users size={12} style={{marginRight:4}}/>{KO?"참석자":SP?"Asistentes":"Attendees"} ({atts.length}{KO?"명":SP?"":""})</div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                          {sortByTenure(atts).reduce((rows,name,i)=>{
                            if(i%3===0) rows.push([]);
                            rows[rows.length-1].push(name);
                            return rows;
                          },[]).map((row,ri)=>(
                            <div key={ri} style={{display:"flex",gap:4,width:"100%"}}>
                              {row.map(name=>{
                                const signed=sigs[name];
                                return <span key={name} style={{display:"inline-flex",alignItems:"center",padding:"4px 12px",borderRadius:20,fontSize:12,fontWeight:600,
                                  background:signed?"rgba(5,150,105,.12)":"rgba(217,119,6,.12)",
                                  color:signed?"var(--gr)":"var(--ye)",whiteSpace:"nowrap"}}>
                                  {name}
                                </span>;
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* 우: 서명버튼 3개씩 + 수정/삭제 */}
                    <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end"}}>
                      {isAdmin&&unsignedSorted.length>0&&(
                        <div style={{display:"flex",flexWrap:"wrap",gap:4,justifyContent:"flex-end"}}>
                          {unsignedSorted.reduce((rows,name,i)=>{
                            if(i%3===0) rows.push([]);
                            rows[rows.length-1].push(name);
                            return rows;
                          },[]).map((row,ri)=>(
                            <div key={ri} style={{display:"flex",gap:4,width:"100%",justifyContent:"flex-end"}}>
                              {row.map(name=>(
                                <button key={name} className="btn bs" style={{background:"#1a1a1a",color:"#fff",fontSize:12,padding:"5px 12px",display:"inline-flex",alignItems:"center",gap:5,whiteSpace:"nowrap"}}
                                  onClick={()=>setSignModal({recId:rec.id,empName:name,quarter:rec.quarter,year:rec.year,attendees:rec.attendees||rec.selected_emps||[]})}>
                                  <PenLine size={13} style={{marginRight:4}}/>{name} {KO?"서명":SP?"Firmar":"Sign"}
                                </button>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                      {!isAdmin&&myUnsigned&&(
                        <button className="btn bs" style={{background:"#1a1a1a",color:"#fff"}}
                          onClick={()=>setSignModal({recId:rec.id,empName:myUnsigned,quarter:rec.quarter,year:rec.year,attendees:rec.attendees||rec.selected_emps||[]})}>
                          <PenLine size={13} style={{marginRight:4}}/>{KO?"내 서명":SP?"Mi firma":"My Signature"}
                        </button>
                      )}
                      {allSigned&&(
                        <button className="btn bs" style={{background:"var(--ac)",color:"#fff",whiteSpace:"nowrap"}} onClick={()=>downloadPdf(rec)}>
                          {KO?"PDF 다운로드":SP?"Descargar PDF":"Download PDF"}
                        </button>
                      )}
                      {isAdmin&&<div style={{display:"flex",gap:6,marginTop:4}}>
                        <button className="btn bs" style={{background:"var(--sf2)",color:"var(--tx2)",border:"1px solid var(--bd)"}} onClick={()=>startEdit(rec)}>{KO?"수정":SP?"Editar":"Edit"}</button>
                        <button className="btn bs bd2" onClick={()=>del(rec.id)}>{KO?"삭제":SP?"Eliminar":"Delete"}</button>
                      </div>}
                    </div>
                  </div>
                </>;
              })()}
            </div>
          ))
        }</>
      )}

      {/* 서명 모달 */}
      {signModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={()=>setSignModal(null)}>
          <div style={{background:"var(--bg)",borderRadius:16,padding:28,minWidth:340,boxShadow:"0 8px 32px rgba(0,0,0,.18)"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{fontWeight:700,fontSize:15,marginBottom:4}}>{KO?"서명":SP?"Firma":"Signature"}</div>
            <div style={{fontSize:13,color:"var(--tx3)",marginBottom:12}}>{signModal.empName}</div>
            <canvas ref={canvasRef} width={420} height={160}
              style={{border:"1px solid var(--bd)",borderRadius:8,width:"100%",touchAction:"none",background:"#fff",cursor:"crosshair"}}
              onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
              onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}/>
            <div style={{display:"flex",gap:8,marginTop:12,justifyContent:"flex-end"}}>
              <button onClick={clearCanvas} style={{padding:"7px 16px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",cursor:"pointer",fontWeight:600,fontSize:13}}>
                {KO?"지우기":SP?"Borrar":"Clear"}
              </button>
              <button onClick={()=>setSignModal(null)} style={{padding:"7px 16px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",cursor:"pointer",fontWeight:600,fontSize:13}}>
                {KO?"취소":SP?"Cancelar":"Cancel"}
              </button>
              <button onClick={submitSignature} disabled={signing} style={{padding:"7px 20px",borderRadius:8,border:"none",background:"#1a1a1a",color:"#fff",cursor:signing?"not-allowed":"pointer",fontWeight:700,fontSize:13,opacity:signing?.7:1}}>
                {signing?"...":(KO?"서명 완료":SP?"Enviar":"Submit")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
